import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ExpandIcon from "@mui/icons-material/ExpandMoreRounded";
import { PageHeader } from "@/components/layout/page";
import { Breadcrumbs } from "@/components/nav/breadcrumbs";
import { CodeBlock } from "@/components/display/code-block";
import { LoadingState, ErrorState, EmptyState } from "@/components/feedback/states";
import { useToast } from "@/components/feedback/toaster";
import { CompatibilityDialog } from "@/components/catalog/compatibility-dialog";
import { RegisterSchemaDialog } from "@/components/catalog/register-schema-dialog";
import { SCHEMA_FORMATS, normalizeSchemaType } from "@/lib/schema-types";
import {
  COMPATIBILITY_MODES,
  useSchemaConfig,
  useSchemaVersions,
  useUpdateSchemaConfig,
} from "@/api/catalog";

export const Route = createFileRoute("/_app/catalog/schemas/$subject")({
  component: SchemaSubjectDetail,
});

function SchemaSubjectDetail() {
  const { subject } = Route.useParams();
  const toast = useToast();
  const { data, isLoading, error, refetch } = useSchemaVersions(subject);
  const config = useSchemaConfig(subject);
  const updateConfig = useUpdateSchemaConfig(subject);
  const [checkOpen, setCheckOpen] = useState(false);
  const [versionOpen, setVersionOpen] = useState(false);

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={() => refetch()} />;

  const mode = config.data?.compatibility ?? "BACKWARD";
  const versions = [...(data ?? [])].sort((a, b) => (b.version ?? 0) - (a.version ?? 0));
  const latest = versions[0];

  return (
    <Stack spacing={2}>
      <PageHeader
        title={subject}
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: "Schema & Lineage", to: "/catalog" },
              { label: "Schemas", to: "/catalog/schemas" },
              { label: subject },
            ]}
          />
        }
        actions={
          <>
            <Button disabled={!latest} onClick={() => setCheckOpen(true)}>
              Check compatibility
            </Button>
            <Button disabled={!latest} variant="contained" onClick={() => setVersionOpen(true)}>
              Register new version
            </Button>
          </>
        }
      />

      <Card sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            select
            size="small"
            label="Compatibility mode"
            value={mode}
            onChange={async (e) => {
              await updateConfig.mutateAsync(e.target.value);
              config.refetch();
              toast.success("Compatibility updated");
            }}
            sx={{ minWidth: 220 }}
          >
            {COMPATIBILITY_MODES.map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </TextField>
          <Typography variant="caption" color="text.secondary">
            Applied to every version of this subject.
          </Typography>
        </Stack>
      </Card>

      <CompatibilityDialog
        open={checkOpen}
        onClose={() => setCheckOpen(false)}
        subject={subject}
        schemaType={latest?.schemaType}
        initialSchema={latest?.schema}
      />
      <RegisterSchemaDialog
        open={versionOpen}
        onClose={() => setVersionOpen(false)}
        subject={subject}
        existingVersion={
          latest
            ? { ...latest, compatibility: config.data?.compatibility ?? latest.compatibility }
            : undefined
        }
        onRegistered={() => refetch()}
      />
      {(data ?? []).length === 0 ? (
        <EmptyState title="No versions" />
      ) : (
        versions.map((v) => (
          <Accordion
            key={v.version ?? String(v.id)}
            defaultExpanded={v.version === latest?.version}
          >
            <AccordionSummary expandIcon={<ExpandIcon />}>
              <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                <Typography variant="subtitle2">v{v.version}</Typography>
                {v.schemaType ? <Chip size="small" label={v.schemaType} /> : null}
                {v.compatibility ? (
                  <Chip size="small" variant="outlined" label={v.compatibility} />
                ) : null}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <CodeBlock
                code={v.schema ?? ""}
                language={
                  normalizeSchemaType(v.schemaType)
                    ? SCHEMA_FORMATS[normalizeSchemaType(v.schemaType)!].language
                    : "text"
                }
              />
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Stack>
  );
}
